import { type FC, useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import useMeasure from 'react-use-measure';
import { motion } from 'framer-motion';

import { getImage } from '@/common/lib/images';
import { parseMeasureFromCssVars } from '@/common/lib/measure-to-css-vars';
import { type MiniGameType } from '@/common/types/dice-game';
import { type GoodsType } from '@/common/types/goods';
import { type PlantTypeText } from '@/common/types/plant';
import { type ResourceTypeText } from '@/common/types/resource';
import { ClaimCashCardAnimation } from '@/components/Animations/ClaimItemAnimation/ClaimCashCardAnimation/ClaimCashCardAnimation';
import { CounterUpDown } from '@/components/CounterUpDown/CounterUpDown';
import { Picture } from '@/components/ui/Picture/Picture';
import { setTabBarHighlight } from '@/redux/reducers/appReducer';
import {
  setActiveBoard,
  setClaimRewardAnimationFinalPosition,
} from '@/redux/reducers/diceGameReducer';
import { useAppDispatch, useAppSelector } from '@/redux/store';

import { DiceGameContext } from '../context';

interface GameItemRewardProps {
  itemName: ResourceTypeText | PlantTypeText | GoodsType;
  value: number;
}

const GameItemReward: FC<GameItemRewardProps> = ({ itemName, value }) => {
  const itemImage = getImage({ name: itemName, field: 'icon' });
  const [ref, measure] = useMeasure({ offsetSize: true, debounce: 500 });
  const { isFlyingCardsReward, setIsFlyingCardsReward } =
    useContext(DiceGameContext);
  const [delayedValue, setDelayedValue] = useState(0);
  const startPoint = useAppSelector(
    (state) => state.diceGame.position.rewards[itemName!],
  );
  const dispatch = useAppDispatch();

  useEffect(() => {
    const reward = itemName?.toLowerCase();

    if (measure.x && measure.y && reward) {
      dispatch(
        setClaimRewardAnimationFinalPosition({
          reward: itemName,
          position: measure,
        }),
      );
    }
  }, [measure]);

  useEffect(() => {
    if (delayedValue === 0) {
      const timeout = setTimeout(() => setDelayedValue(value), 500);

      return (): void => clearTimeout(timeout);
    }
    setDelayedValue(value);
  }, [value]);

  const isAnimatingFly = ['Material', 'Product', 'Resource'].includes(
    isFlyingCardsReward as MiniGameType,
  );

  const stockFinalPosition = parseMeasureFromCssVars('--tabbar-stock');

  return (
    <motion.div
      className="flex flex-col items-center font-bold"
      animate={{
        opacity: isAnimatingFly ? 0 : 1,
      }}
      initial={{
        opacity: 0,
      }}
      transition={{
        delay: isAnimatingFly ? 1.5 : 0.6,
        duration: 0.25,
      }}
    >
      <motion.div
        className="size-12 max-w-12 max-h-12 shrink-0 relative z-[3]"
        animate={{
          scale: isAnimatingFly ? 1.4 : 1,
        }}
        transition={{
          delay: 0.5,
          duration: 0.3,
        }}
      >
        <Picture
          img={itemImage?.img}
          webp={itemImage?.webp}
          alt={itemName}
          ref={ref}
          className="w-full h-full"
        />
        {stockFinalPosition &&
          startPoint &&
          isAnimatingFly &&
          createPortal(
            <motion.div
              className="fixed w-[min(7.252svh,_67px)] h-[min(7.252svh,_67px)]"
              animate={{
                opacity: 1,
              }}
              initial={{
                opacity: 0,
              }}
              transition={{
                delay: 0.55,
                duration: 0.3,
              }}
              style={{
                left: measure.x - measure.width * 0.2,
                top: measure.y - measure.height * 0.2,
              }}
            >
              <ClaimCashCardAnimation
                icon={{ img: itemImage?.img, webp: itemImage?.webp }}
                reward={itemName}
                finalPositon={stockFinalPosition}
                type="arc"
                onAnimationEnd={() => {
                  setIsFlyingCardsReward(null);
                  dispatch(setActiveBoard('dice'));
                  dispatch(setTabBarHighlight(null));
                }}
                scale={[1, 1, 1, 1, 0.5]}
              />
            </motion.div>,
            document.getElementById('root')!,
          )}
      </motion.div>
      <CounterUpDown
        value={isAnimatingFly ? 0 : delayedValue || 0}
        delay={700}
        duration={1.1}
      />
    </motion.div>
  );
};

export { GameItemReward };
